import React, { useEffect, useMemo, useState } from 'react'
import { getSystemOrganizations, saveOrganization, togglesystemOrganization } from '../../services/organisation.service'
import { GridActionsCellItem } from '@mui/x-data-grid';
import { Edit } from '@mui/icons-material';
import { Chip, Tooltip } from '@mui/material';
import ErrorIcon from "@mui/icons-material/DoDisturbAlt";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import { ResponsiveRow } from '../../constants/layout.constants';
import MuiDataGrid from './MuiDataGrid';
import ConfirmationModal from '../modals/confirmation';
import { issueResponseMessage } from '../../actions/message';
import { isNullUndefined } from '../../util/util';
import AddNewOrganization from '../modals/addNewOrganization';

function ManageSystemOrganizationsTab() {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [editOrganizationModal, setEditOrganizationModal] = useState(false);
  const [task, setTask] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(true);

  const handleViewEditClick = (org, action) => {
    // Handle view click logic here (e.g., open a modal to view details)
    setTask(action);
    setSelectedOrganization(org);
    if(action === "Edit") {
      setEditOrganizationModal(true);
    }
  };

  const handleConfirmClose = () => {
    setConfirm(false);
    setSelectedOrganization(null);
  };

  const handleTogglesystemOrganization = async (id) => {
    try {
      const res = await togglesystemOrganization(id);
      issueResponseMessage(res);
      handleConfirmClose();
      fetchSysOrganizations();
    } catch (error) {
      console.error("Error making organization a system organization:", error);
    }
  };

  const columns = useMemo(() => [
    {
      headerName: "Name",
      field: "name",
      minWidth: 200,
      flex: 2,
    },
    {
      headerName: "Description",
      field: "description",
      minWidth: 150,
      flex: 1.5,
    },
    { headerName: "Status", field: "status", minWidth: 100, flex: 1.5 },
    { headerName: "Tax ID", field: "taxId", minWidth: 100, flex: 1.5 },
    {
      headerName: "Has Address",
      field: "addressId",
      minWidth: 175,
      flex: 1.75,
      renderCell: (params) => {
        return (
          <Tooltip
            title={
              params.row.addressId ? params.row.address.fullAddress1 : "No"
            }
          >
            <Chip
              icon={params.row.addressId ? <VerifiedUserIcon /> : <ErrorIcon />}
              label={
                params.row.addressId ? params.row.address.fullAddress1 : "No"
              }
              color={params.row.addressId ? "success" : "error"}
              size="small"
              sx={{ flexShrink: 0 }}
            />
          </Tooltip>
        );
      },
    },
    {
      headerName: "Has Contacts",
      field: "contact",
      minWidth: 175,
      flex: 1.75,
      renderCell: (params) => {
        return (
          <Tooltip
            title={
              params.row.contact && params.row.contact.length > 0
                ? params.row.contact.map((contact) => contact.name).join(", ")
                : "No"
            }
          >
            <Chip
              icon={
                params.row.contact && params.row.contact.length > 0 ? (
                  <VerifiedUserIcon />
                ) : (
                  <ErrorIcon />
                )
              }
              label={
                params.row.contact && params.row.contact.length > 0
                  ? params.row.contact.map((contact) => contact.name).join(", ")
                  : "No"
              }
              color={
                params.row.contact && params.row.contact.length > 0
                  ? "success"
                  : "error"
              }
              size="small"
              sx={{ flexShrink: 0 }}
            />
          </Tooltip>
        );
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 100,
      type: "actions",
      getActions: (params) => {
        let arr = [];
        arr.push(
          <GridActionsCellItem
            icon={<Edit />}
            onClick={() => handleViewEditClick(params.row, "Edit")}
            label="Edit"
            showInMenu
          />,
        );
        arr.push(
          <GridActionsCellItem
            icon={<CorporateFareIcon />}
            label={params.row.systemOrganization ? "Remove as System Organization" : "Add as System Organization"}
            onClick={() => {
              setSelectedOrganization(params.row);
              setConfirm(true);
            }}
            showInMenu
          />,
        );
        return arr;
      },
    },
  ], []);
  
  const fetchSysOrganizations = () => {
    setLoading(true);
    getSystemOrganizations().then((data) => {
      setOrganizations(data);
    })
    .finally(() => {
      setLoading(false);
    });
  }

  const handleEditOrgClose = () => {
    setEditOrganizationModal(false);
    setTask(null)
  }

  const handleSaveOrg = (savedOrg) => {
    return saveOrganization(savedOrg).then((res) => {
      issueResponseMessage(res);
      // A rejected save resolves to undefined (the service raises the error
      // toast). Leave the form open so the details can be corrected.
      if (isNullUndefined(res) || res.type === "error") {
        return res;
      }
      handleEditOrgClose();
      fetchSysOrganizations();
      return res;
    });
  }

  useEffect(() => {
    fetchSysOrganizations();
  }, [])

  return (
    <ResponsiveRow>
      <MuiDataGrid
        dataGridColumns={columns}
        dataGridRows={organizations}
        loading={loading}
        height="65vh"
        totalRows={organizations ? organizations.length : 0}
        serverPagination={false}
        noSelection
      />
      {confirm && (
        <ConfirmationModal
          showConfirmationModal={confirm}
          handleConfirmationClose={handleConfirmClose}
          title={"Confirm toggle of system organization status"}
          severity={"warning"}
          body={
            `Are you sure you want to ${selectedOrganization.systemOrganization ? "remove" : "add"} system organization status for organization: ` +
            selectedOrganization.name
          }
          positiveText={"Confirm"}
          negativeText={"Cancel"}
          positiveAction={() =>
            handleTogglesystemOrganization(selectedOrganization.id)
          }
          negativeAction={handleConfirmClose}
          negativeActionText={"Cancel"}
        />
      )}
      {task === "Edit" && (
        <AddNewOrganization
          openModal={editOrganizationModal}
          closeModal={handleEditOrgClose}
          task={task}
          handleSave={true}
          selectedOrg={selectedOrganization}
          saveOrg={handleSaveOrg}
        />
      )}
    </ResponsiveRow>
  );
}

export default ManageSystemOrganizationsTab